import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/pl';
import axios from '../../../axiosConfig';

import OwnerLayout from '../OwnerLayout';
import { OwnerContext } from '../../../context/OwnerContext';

import {
    Calendar,
    CalendarEvent,
    useCalendarNavigation,
    useCalendarSelection,
    useResponsiveCalendar,
} from '../../calendar';
import '../../calendar/styles/index.css';

import AdditionalInfoModal from './AdditionalInfoModal';
import DeleteConfirmationModal from '../../trainer/DeleteConfirmationModal';

import '../../../styles/OwnerCalendar.css';
import '../../../styles/OwnerCalendarBookingBar.css';

dayjs.locale('pl');
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const DAY_OF_WEEK_MAPPING = {
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
    7: 'SUNDAY',
};

const formatDurationForDisplay = (duration) => {
    if (!duration) return '60 min';
    if (typeof duration === 'number') return `${duration} min`;

    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return '60 min';

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const totalMinutes = hours * 60 + minutes;

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
        return `${hours}h`;
    }
    return `${totalMinutes} min`;
};

const OwnerCalendarControls = ({
                                   trainers,
                                   ownerDogs,
                                   services,
                                   selectedTrainerId,
                                   selectedDogId,
                                   selectedServiceId,
                                   onTrainerChange,
                                   onDogChange,
                                   onServiceChange,
                                   onMultipleBookingClick,
                                   bookingMode,
                               }) => {
    return (
        <div className="owner-calendar-controls">
            <div className="owner-controls-row">
                <div className="owner-control-item">
                    <select
                        className={`owner-dropdown ${selectedTrainerId ? 'has-value' : ''}`}
                        value={selectedTrainerId || ''}
                        onChange={(e) => onTrainerChange(e.target.value)}
                    >
                        <option value="">Wybierz trenera</option>
                        {trainers.map((trainer) => (
                            <option key={trainer.id} value={trainer.id}>
                                {trainer.firstName} {trainer.lastName}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="owner-control-item">
                    <select
                        className={`owner-dropdown ${selectedDogId ? 'has-value' : ''}`}
                        value={selectedDogId || ''}
                        onChange={(e) => onDogChange(e.target.value)}
                    >
                        <option value="">Wybierz pupila</option>
                        {ownerDogs.map((dog) => (
                            <option key={dog.id} value={dog.id}>
                                {dog.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="owner-control-item">
                    <select
                        className={`owner-dropdown ${selectedServiceId ? 'has-value' : ''}`}
                        value={selectedServiceId || ''}
                        onChange={(e) => onServiceChange(e.target.value)}
                    >
                        <option value="">Wybierz rodzaj treningu</option>
                        {services.map((service) => (
                            <option key={service.id} value={service.id}>
                                {service.serviceName} ({formatDurationForDisplay(service.calendarDuration)})
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

const SelectionPlaceholder = ({ isMobileView = false }) => {
    return (
        <div className={isMobileView ? 'owner-mobile-placeholder' : 'owner-calendar-placeholder'}>
            <div className={isMobileView ? 'owner-mobile-no-selection' : 'owner-no-selection'}>
                <div className={isMobileView ? 'owner-mobile-no-selection-icon' : 'owner-no-selection-icon'}>
                    📅
                </div>
                <div className={isMobileView ? 'owner-mobile-no-selection-text' : 'owner-no-selection-text'}>
                    Wybierz trenera, pupila i rodzaj treningu
                    {isMobileView ? ' ' : <br />}
                    aby wyświetlić kalendarz
                </div>
            </div>
        </div>
    );
};

const BookingBar = ({
                        selectedSlots,
                        onClearSelection,
                        onBookSlots,
                        isBooking,
                    }) => {
    if (selectedSlots.length === 0) return null;

    return (
        <div className="owner-booking-bar">
            <div className="owner-booking-bar__info">
        <span className="owner-booking-bar__count">
          Wybrano terminów: <strong>{selectedSlots.length}</strong>
        </span>
            </div>
            <div className="owner-booking-bar__actions">
                <button
                    className="owner-booking-bar__clear"
                    onClick={onClearSelection}
                    disabled={isBooking}
                >
                    Wyczyść wybór
                </button>
                <button
                    className="owner-booking-bar__submit"
                    onClick={onBookSlots}
                    disabled={isBooking}
                >
                    {isBooking ? 'Rezerwowanie...' : `Zarezerwuj (${selectedSlots.length})`}
                </button>
            </div>
        </div>
    );
};

const OwnerCalendarContent = ({
                                  trainings,
                                  trainingLocks,
                                  confirmedTrainings,
                                  trainerGroupTrainings,
                                  dogGroupTrainings,
                                  trainers,
                                  ownerDogs,
                                  services,
                                  workingHours,
                                  schoolSettings,
                                  selectedTrainer,
                                  selectedDogId,
                                  selectedServiceId,
                                  serviceDurationMinutes,
                                  onTrainerChange,
                                  onDogChange,
                                  onServiceChange,
                                  onBookSlots,
                                  onCancelTraining,
                                  onWeekChange,
                                  isBooking,
                                  bookingMode,
                                  onToggleBookingMode,
                              }) => {
    const { shouldUseMobileView } = useResponsiveCalendar();
    const { currentWeekStart } = useCalendarNavigation();
    const { selectedSlots, toggleSlot, clearSelection, isSlotSelected } = useCalendarSelection();
    const [selectedDay, setSelectedDay] = useState(dayjs());

    // Fetch data when week changes
    useEffect(() => {
        if (selectedTrainer && selectedDogId && selectedServiceId) {
            onWeekChange(currentWeekStart);
        }
    }, [currentWeekStart, selectedTrainer, selectedDogId, selectedServiceId, onWeekChange]);

    // Update selectedDay to stay within current week when navigating
    useEffect(() => {
        const weekEnd = currentWeekStart.add(6, 'day');
        if (selectedDay.isBefore(currentWeekStart, 'day') || selectedDay.isAfter(weekEnd, 'day')) {
            // If selected day is outside current week, reset to first day of week
            setSelectedDay(currentWeekStart);
        }
    }, [currentWeekStart, selectedDay]);

    const isReady = selectedTrainer && selectedDogId && selectedServiceId;

    // Calculate events based on current week
    const events = useMemo(() => {
        if (!selectedTrainer || !workingHours.length) {
            return [];
        }

        const eventsList = [];
        const now = dayjs();

        const DAY_MAPPING = {
            1: 'MONDAY',
            2: 'TUESDAY',
            3: 'WEDNESDAY',
            4: 'THURSDAY',
            5: 'FRIDAY',
            6: 'SATURDAY',
            7: 'SUNDAY',
        };

        const isSlotOccupied = (slotStart, slotEnd) => {
            const start = dayjs(slotStart);
            const end = dayjs(slotEnd);

            const hasTraining = trainings.some((t) => {
                const tStart = dayjs(t.startTime);
                const tEnd = dayjs(t.endTime);
                return start.isBefore(tEnd) && end.isAfter(tStart);
            });
            if (hasTraining) return true;

            const hasConfirmed = confirmedTrainings.some((ct) => {
                const ctStart = dayjs(ct.startTime);
                const ctEnd = dayjs(ct.endTime);
                return start.isBefore(ctEnd) && end.isAfter(ctStart);
            });
            if (hasConfirmed) return true;

            const hasLock = trainingLocks.some((lock) => {
                const lockStart = dayjs(lock.startTime);
                const lockEnd = dayjs(lock.endTime);
                return start.isBefore(lockEnd) && end.isAfter(lockStart);
            });
            if (hasLock) return true;

            // Check if trainer has a group training at this time
            const hasGroupTraining = trainerGroupTrainings.some((gt) => {
                const gtStart = dayjs(gt.startTime);
                const gtEnd = dayjs(gt.endTime);
                return start.isBefore(gtEnd) && end.isAfter(gtStart);
            });
            if (hasGroupTraining) return true;

            return false;
        };

        // Add owner's trainings
        trainings.forEach((training) => {
            const isConfirmed = training.confirmed;
            const canCancel = dayjs(training.startTime).isAfter(now);

            eventsList.push({
                id: training.id,
                slotId: `training-${training.id}`,
                startTime: training.startTime,
                endTime: training.endTime,
                type: isConfirmed ? 'owner-confirmed' : 'owner-pending',
                isOwnerConfirmed: isConfirmed,
                isOwnerPending: !isConfirmed,
                canBook: false,
                canCancel,
                bookedTraining: training,
                displayTitle: isConfirmed ? 'Twoja rezerwacja' : 'Oczekuje',
            });
        });

        // Add locks
        trainingLocks.forEach((lock) => {
            eventsList.push({
                id: lock.id,
                slotId: `lock-${lock.id}`,
                startTime: lock.startTime,
                endTime: lock.endTime,
                type: 'locked',
                isLocked: true,
                canBook: false,
                displayTitle: 'Niedostępne',
            });
        });

        // Add other confirmed trainings
        confirmedTrainings.forEach((ct) => {
            const isOwnerTraining = trainings.some((t) => t.id === ct.id);
            if (!isOwnerTraining) {
                eventsList.push({
                    id: ct.id,
                    slotId: `confirmed-${ct.id}`,
                    startTime: ct.startTime,
                    endTime: ct.endTime,
                    type: 'confirmed-other',
                    isConfirmedOther: true,
                    canBook: false,
                    displayTitle: 'Zajęte',
                });
            }
        });

        // Add dog's group trainings (blue cards - owner's dog participates)
        dogGroupTrainings.forEach((gt) => {
            eventsList.push({
                id: gt.id,
                slotId: `dog-group-${gt.id}`,
                startTime: gt.startTime,
                endTime: gt.endTime,
                type: 'dog-group-training',
                isDogGroupTraining: true,
                canBook: false,
                displayTitle: 'Trening grupowy',
                groupName: gt.groupName || gt.trainingGroup?.name,
                groupTraining: gt,
            });
        });

        // Add trainer's group trainings where dog is NOT participating (show as "Zajęte")
        trainerGroupTrainings.forEach((gt) => {
            // Check if this group training is already in dog's group trainings
            const isDogInGroup = dogGroupTrainings.some((dgt) => dgt.id === gt.id);
            if (!isDogInGroup) {
                eventsList.push({
                    id: gt.id,
                    slotId: `trainer-group-${gt.id}`,
                    startTime: gt.startTime,
                    endTime: gt.endTime,
                    type: 'trainer-group-training',
                    isTrainerGroupTraining: true,
                    canBook: false,
                    displayTitle: 'Zajęte',
                });
            }
        });

        // Generate free slots for the current week
        for (let i = 0; i < 7; i++) {
            const dayDate = currentWeekStart.add(i, 'day');
            const dayKey = DAY_MAPPING[dayDate.isoWeekday()];
            const dayWorkingHours = workingHours.find((wh) => wh.dayOfWeek === dayKey);

            if (!dayWorkingHours) continue;

            const [openHour, openMin] = dayWorkingHours.openingTime.split(':').map(Number);
            const [closeHour, closeMin] = dayWorkingHours.closingTime.split(':').map(Number);

            let slotTime = dayDate.hour(openHour).minute(openMin).second(0);
            const closeTime = dayDate.hour(closeHour).minute(closeMin).second(0);

            while (slotTime.add(serviceDurationMinutes, 'minute').isSameOrBefore(closeTime)) {
                const slotEnd = slotTime.add(serviceDurationMinutes, 'minute');
                const slotStartStr = slotTime.format('YYYY-MM-DDTHH:mm:ss');
                const slotEndStr = slotEnd.format('YYYY-MM-DDTHH:mm:ss');

                if (!isSlotOccupied(slotStartStr, slotEndStr)) {
                    const isPast = slotTime.isBefore(now);

                    // Check minBookingDaysInAdvance from school settings
                    const minDaysInAdvance = schoolSettings?.minBookingDaysInAdvance || 0;
                    const earliestBookableDate = now.add(minDaysInAdvance, 'day').startOf('day');
                    const isTooSoon = slotTime.isBefore(earliestBookableDate);

                    // Check if month is unlocked
                    const monthNames = [
                        'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                        'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
                    ];
                    const slotMonth = monthNames[slotTime.month()];
                    const isMonthLocked = schoolSettings?.unlockedMonths?.length > 0 &&
                        !schoolSettings.unlockedMonths.includes(slotMonth);

                    const canBook = !isPast && !isTooSoon && !isMonthLocked;
                    const isUnavailable = isPast || isTooSoon || isMonthLocked;

                    let displayTitle = 'Wolny termin';
                    if (isPast) {
                        displayTitle = 'Niedostępne';
                    } else if (isTooSoon) {
                        displayTitle = 'Niedostępne';
                    } else if (isMonthLocked) {
                        displayTitle = 'Miesiąc zamknięty';
                    }

                    eventsList.push({
                        id: `free-${slotStartStr}`,
                        slotId: `free-${slotStartStr}`,
                        startTime: slotStartStr,
                        endTime: slotEndStr,
                        type: 'free',
                        canBook,
                        isPast,
                        isTooSoon,
                        isMonthLocked,
                        isUnavailable,
                        displayTitle,
                    });
                }

                slotTime = slotTime.add(serviceDurationMinutes, 'minute');
            }
        }

        return eventsList;
    }, [selectedTrainer, workingHours, trainings, trainingLocks, confirmedTrainings, trainerGroupTrainings, dogGroupTrainings, serviceDurationMinutes, currentWeekStart, schoolSettings]);

    const getEventVariant = useCallback((event) => {
        if (event.isOwnerConfirmed) return 'confirmed';
        if (event.isOwnerPending) return 'pending';
        if (event.isDogGroupTraining) return 'group'; // Blue color for dog's group trainings
        if (event.isLocked) return 'locked';
        if (event.isConfirmedOther || event.isTrainerGroupTraining) return 'occupied'; // Zajęte style
        if (event.isUnavailable || event.isPast) return 'unavailable';
        if (isSlotSelected(event.slotId)) return 'free-selected';
        return 'free';
    }, [isSlotSelected]);

    const handleEventClick = useCallback((event) => {
        if (event.type === 'free' && event.canBook) {
            toggleSlot({
                id: event.slotId,
                startTime: event.startTime,
                endTime: event.endTime,
                trainerId: selectedTrainer?.id,
            });
        } else if (event.canCancel && event.bookedTraining) {
            onCancelTraining(event.bookedTraining);
        }
    }, [toggleSlot, selectedTrainer, onCancelTraining]);

    const handleBooking = useCallback(() => {
        if (selectedSlots.length > 0) {
            onBookSlots(selectedSlots);
            clearSelection();
        }
    }, [selectedSlots, onBookSlots, clearSelection]);

    const eventDays = useMemo(() => {
        return events
            .map((e) => dayjs(e.startTime).format('YYYY-MM-DD'))
            .filter((v, i, a) => a.indexOf(v) === i);
    }, [events]);

    const renderEvent = useCallback((event) => {
        const variant = getEventVariant(event);
        const isSelected = isSlotSelected(event.slotId);

        return (
            <CalendarEvent
                key={event.id || event.slotId}
                event={event}
                variant={variant}
                position={event.position}
                overlap={event.overlap}
                selectable={event.type === 'free' && event.canBook}
                selected={isSelected}
                onSelect={() => handleEventClick(event)}
                onClick={() => handleEventClick(event)}
            />
        );
    }, [getEventVariant, isSlotSelected, handleEventClick]);

    if (shouldUseMobileView()) {
        return (
            <>
                <OwnerCalendarControls
                    trainers={trainers}
                    ownerDogs={ownerDogs}
                    services={services}
                    selectedTrainerId={selectedTrainer?.id}
                    selectedDogId={selectedDogId}
                    selectedServiceId={selectedServiceId}
                    onTrainerChange={onTrainerChange}
                    onDogChange={onDogChange}
                    onServiceChange={onServiceChange}
                    onMultipleBookingClick={onToggleBookingMode}
                    bookingMode={bookingMode}
                />

                {isReady ? (
                    <>
                        <Calendar.MobileNavigation />
                        <Calendar.MobileWeekStrip
                            selectedDay={selectedDay}
                            onDaySelect={setSelectedDay}
                            eventDays={eventDays}
                        />
                        <Calendar.MobileDayView
                            selectedDay={selectedDay}
                            events={events}
                            onEventClick={handleEventClick}
                        />
                    </>
                ) : (
                    <SelectionPlaceholder isMobileView={true} />
                )}

                <BookingBar
                    selectedSlots={selectedSlots}
                    onClearSelection={clearSelection}
                    onBookSlots={handleBooking}
                    isBooking={isBooking}
                />
            </>
        );
    }

    return (
        <>
            <OwnerCalendarControls
                trainers={trainers}
                ownerDogs={ownerDogs}
                services={services}
                selectedTrainerId={selectedTrainer?.id}
                selectedDogId={selectedDogId}
                selectedServiceId={selectedServiceId}
                onTrainerChange={onTrainerChange}
                onDogChange={onDogChange}
                onServiceChange={onServiceChange}
                onMultipleBookingClick={onToggleBookingMode}
                bookingMode={bookingMode}
            />

            {isReady ? (
                <>
                    <Calendar.Navigation />
                    <Calendar.Grid
                        events={events}
                        renderEvent={renderEvent}
                        onEventClick={handleEventClick}
                    />
                </>
            ) : (
                <SelectionPlaceholder />
            )}

            <BookingBar
                selectedSlots={selectedSlots}
                onClearSelection={clearSelection}
                onBookSlots={handleBooking}
                isBooking={isBooking}
            />
        </>
    );
};

const OwnerCalendar = () => {
    const { owner } = useContext(OwnerContext);
    const [searchParams] = useSearchParams();

    const [trainers, setTrainers] = useState([]);
    const [ownerDogs, setOwnerDogs] = useState([]);
    const [services, setServices] = useState([]);
    const [workingHours, setWorkingHours] = useState([]);
    const [schoolSettings, setSchoolSettings] = useState(null);

    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [selectedDogId, setSelectedDogId] = useState(null);
    const [selectedServiceId, setSelectedServiceId] = useState(null);

    const [trainings, setTrainings] = useState([]);
    const [confirmedTrainings, setConfirmedTrainings] = useState([]);
    const [trainingLocks, setTrainingLocks] = useState([]);
    const [trainerGroupTrainings, setTrainerGroupTrainings] = useState([]);
    const [dogGroupTrainings, setDogGroupTrainings] = useState([]); // Group trainings where owner's dog participates

    const [bookingMode, setBookingMode] = useState('single');
    const [isBooking, setIsBooking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [additionalInfoModalOpen, setAdditionalInfoModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);
    const [pendingBookingSlots, setPendingBookingSlots] = useState([]);

    // Track current viewed week for refresh after booking
    const [currentViewedWeek, setCurrentViewedWeek] = useState(dayjs().startOf('isoWeek'));

    const selectedService = useMemo(() => {
        return services.find((s) => s.id === selectedServiceId);
    }, [services, selectedServiceId]);

    const parseDurationToMinutes = (duration) => {
        if (!duration) return 60;
        if (typeof duration === 'number') return duration;

        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        if (!match) return 60;

        const hours = parseInt(match[1] || '0', 10);
        const minutes = parseInt(match[2] || '0', 10);
        return hours * 60 + minutes;
    };

    const serviceDurationMinutes = useMemo(() => {
        if (!selectedService) return 60;
        return parseDurationToMinutes(selectedService.calendarDuration) || 60;
    }, [selectedService]);

    const getAuthHeader = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem('jwt')}`,
    }), []);

    const fetchTrainers = useCallback(async () => {
        if (!owner?.schoolId) return;

        try {
            const response = await axios.get(`/api/v1/trainer/find-all/${owner.schoolId}`, {
                headers: getAuthHeader(),
            });
            setTrainers(response.data || []);
        } catch (error) {
            console.error('Failed to fetch trainers:', error);
        }
    }, [owner?.schoolId, getAuthHeader]);

    const fetchOwnerDogs = useCallback(async () => {
        if (!owner?.id) return;

        try {
            const response = await axios.get(`/api/v1/dog/${owner.id}/list`, {
                headers: getAuthHeader(),
            });
            setOwnerDogs(response.data || []);
        } catch (error) {
            console.error('Failed to fetch dogs:', error);
        }
    }, [owner?.id, getAuthHeader]);

    const fetchServices = useCallback(async () => {
        if (!owner?.schoolId) return;

        try {
            const response = await axios.get(`/api/v1/services/school/${owner.schoolId}`, {
                headers: getAuthHeader(),
            });
            setServices(response.data || []);
        } catch (error) {
            console.error('Failed to fetch services:', error);
        }
    }, [owner?.schoolId, getAuthHeader]);

    const fetchSchoolSettings = useCallback(async () => {
        if (!owner?.schoolId) return;

        try {
            const response = await axios.get(`/api/v1/school/${owner.schoolId}/settings`, {
                headers: getAuthHeader(),
            });
            setSchoolSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch school settings:', error);
        }
    }, [owner?.schoolId, getAuthHeader]);

    const fetchWorkingHours = useCallback(async () => {
        if (!selectedTrainer?.id) return;

        try {
            const response = await axios.get(`/api/v1/trainer/get-working-hours/${selectedTrainer.id}`, {
                headers: getAuthHeader(),
            });
            setWorkingHours(response.data || []);
        } catch (error) {
            console.error('Failed to fetch working hours:', error);
        }
    }, [selectedTrainer?.id, getAuthHeader]);

    const fetchCalendarData = useCallback(async (weekStart) => {
        if (!selectedTrainer?.id || !selectedDogId) return;

        setIsLoading(true);
        setCurrentViewedWeek(weekStart);

        const startTime = weekStart.startOf('isoWeek').format('YYYY-MM-DDTHH:mm:ss');
        const endTime = weekStart.endOf('isoWeek').format('YYYY-MM-DDTHH:mm:ss');

        try {
            const [trainingsRes, confirmedRes, locksRes, trainerGroupRes, dogGroupRes] = await Promise.all([
                // Owner's trainings for this dog with this trainer
                axios.get(`/api/v1/training/getAllTrainingsForDogForTrainer/${selectedDogId}/${selectedTrainer.id}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Confirmed trainings for this trainer/dog combination (to show as "occupied")
                axios.get(`/api/v1/training/${selectedTrainer.id}/${selectedDogId}/getConfirmedTrainingsForTrainer`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Trainer's locked slots
                axios.get(`/api/v1/training/lock/${selectedTrainer.id}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Trainer's group trainings (to block those slots as "Zajęte")
                axios.get(`/api/v1/group-trainings/trainer/${selectedTrainer.id}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Dog's group trainings (to show as blue cards - owner's dog participates)
                axios.get(`/api/v1/group-trainings/dog/${selectedDogId}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
            ]);

            setTrainings(trainingsRes.data || []);
            setConfirmedTrainings(confirmedRes.data || []);
            setTrainingLocks(locksRes.data || []);

            // Transform trainer's group trainings to have startTime/endTime for compatibility
            const transformedTrainerGroupTrainings = (trainerGroupRes.data || []).map(gt => ({
                ...gt,
                startTime: gt.scheduledAt,
                endTime: dayjs(gt.scheduledAt).add(gt.durationMinutes || 60, 'minute').toISOString(),
            }));
            setTrainerGroupTrainings(transformedTrainerGroupTrainings);

            // Transform dog's group trainings
            const transformedDogGroupTrainings = (dogGroupRes.data || []).map(gt => ({
                ...gt,
                startTime: gt.scheduledAt,
                endTime: dayjs(gt.scheduledAt).add(gt.durationMinutes || 60, 'minute').toISOString(),
            }));
            setDogGroupTrainings(transformedDogGroupTrainings);
        } catch (error) {
            console.error('Failed to fetch calendar data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedTrainer?.id, selectedDogId, getAuthHeader]);

    useEffect(() => {
        fetchTrainers();
        fetchOwnerDogs();
        fetchServices();
        fetchSchoolSettings();
    }, [fetchTrainers, fetchOwnerDogs, fetchServices, fetchSchoolSettings]);

    useEffect(() => {
        fetchWorkingHours();
    }, [fetchWorkingHours]);

    useEffect(() => {
        const trainerIdParam = searchParams.get('trainerId');
        if (trainerIdParam && trainers.length > 0) {
            const trainer = trainers.find((t) => t.id === trainerIdParam);
            if (trainer) {
                setSelectedTrainer(trainer);
            }
        }
    }, [searchParams, trainers]);

    useEffect(() => {
        if (selectedTrainer && selectedDogId && selectedServiceId) {
            fetchCalendarData(dayjs().startOf('isoWeek'));
        }
    }, [selectedTrainer, selectedDogId, selectedServiceId, fetchCalendarData]);

    const handleTrainerChange = useCallback((trainerId) => {
        const trainer = trainers.find((t) => t.id === trainerId);
        setSelectedTrainer(trainer || null);
    }, [trainers]);

    const handleDogChange = useCallback((dogId) => {
        setSelectedDogId(dogId || null);
    }, []);

    const handleServiceChange = useCallback((serviceId) => {
        setSelectedServiceId(serviceId || null);
    }, []);

    const handleToggleBookingMode = useCallback(() => {
        setBookingMode((prev) => (prev === 'single' ? 'multiple' : 'single'));
    }, []);

    const handleBookSlots = useCallback((slots) => {
        setPendingBookingSlots(slots);
        setAdditionalInfoModalOpen(true);
    }, []);

    const handleConfirmBooking = useCallback(async (additionalInfo = '') => {
        if (!pendingBookingSlots.length || !selectedTrainer?.id || !selectedDogId) return;

        setIsBooking(true);
        setAdditionalInfoModalOpen(false);

        try {
            if (pendingBookingSlots.length === 1) {
                const slot = pendingBookingSlots[0];
                await axios.post('/api/v1/training/requestBookingTraining', {
                    trainerId: selectedTrainer.id,
                    dogId: selectedDogId,
                    hallId: null,
                    serviceId: selectedServiceId,
                    additionalInfo,
                    startTime: slot.startTime,
                    endTime: slot.endTime || dayjs(slot.startTime).add(serviceDurationMinutes, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
                }, {
                    headers: getAuthHeader(),
                });
            } else {
                const requests = pendingBookingSlots.map((slot) => ({
                    trainerId: selectedTrainer.id,
                    dogId: selectedDogId,
                    hallId: null,
                    serviceId: selectedServiceId,
                    startTime: slot.startTime,
                    endTime: slot.endTime || dayjs(slot.startTime).add(serviceDurationMinutes, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
                    additionalInfo,
                }));

                await axios.post('/api/v1/training/requestMultipleBookingTraining', requests, {
                    headers: getAuthHeader(),
                });
            }

            setPendingBookingSlots([]);
            // Refresh the currently viewed week
            fetchCalendarData(currentViewedWeek);
        } catch (error) {
            console.error('Failed to book training:', error);
        } finally {
            setIsBooking(false);
        }
    }, [pendingBookingSlots, selectedTrainer, selectedDogId, selectedServiceId, serviceDurationMinutes, getAuthHeader, fetchCalendarData, currentViewedWeek]);

    const handleCancelTraining = useCallback((training) => {
        setTrainingToDelete(training);
        setDeleteModalOpen(true);
    }, []);

    const handleConfirmCancel = useCallback(async () => {
        if (!trainingToDelete) return;

        try {
            await axios.post(`/api/v1/training/${trainingToDelete.id}/decline`, {}, {
                headers: getAuthHeader(),
            });

            setDeleteModalOpen(false);
            setTrainingToDelete(null);
            // Refresh the currently viewed week
            fetchCalendarData(currentViewedWeek);
        } catch (error) {
            console.error('Failed to cancel training:', error);
        }
    }, [trainingToDelete, getAuthHeader, fetchCalendarData, currentViewedWeek]);

    return (
        <OwnerLayout>
            <div className="owner-calendar-container">
                <div className="owner-calendar-header">
                    <h1 className="owner-calendar-title">Zapisy na treningi</h1>
                </div>

                <Calendar
                    trainerId={selectedTrainer?.id}
                    schoolId={owner?.schoolId}
                    workingHours={workingHours}
                    basePath="/owner/calendar"
                    multiSelect={true}
                    maxSelections={20}
                    features={{
                        slotSelection: true,
                        showCurrentTime: true,
                    }}
                >
                    <OwnerCalendarContent
                        trainings={trainings}
                        trainingLocks={trainingLocks}
                        confirmedTrainings={confirmedTrainings}
                        trainerGroupTrainings={trainerGroupTrainings}
                        dogGroupTrainings={dogGroupTrainings}
                        trainers={trainers}
                        ownerDogs={ownerDogs}
                        services={services}
                        workingHours={workingHours}
                        schoolSettings={schoolSettings}
                        selectedTrainer={selectedTrainer}
                        selectedDogId={selectedDogId}
                        selectedServiceId={selectedServiceId}
                        serviceDurationMinutes={serviceDurationMinutes}
                        onTrainerChange={handleTrainerChange}
                        onDogChange={handleDogChange}
                        onServiceChange={handleServiceChange}
                        onBookSlots={handleBookSlots}
                        onCancelTraining={handleCancelTraining}
                        onWeekChange={fetchCalendarData}
                        isBooking={isBooking}
                        bookingMode={bookingMode}
                        onToggleBookingMode={handleToggleBookingMode}
                    />
                </Calendar>

                {additionalInfoModalOpen && (
                    <AdditionalInfoModal
                        isOpen={additionalInfoModalOpen}
                        onClose={() => setAdditionalInfoModalOpen(false)}
                        onConfirm={handleConfirmBooking}
                        slotsCount={pendingBookingSlots.length}
                    />
                )}

                {deleteModalOpen && (
                    <DeleteConfirmationModal
                        isOpen={deleteModalOpen}
                        onClose={() => {
                            setDeleteModalOpen(false);
                            setTrainingToDelete(null);
                        }}
                        onConfirm={handleConfirmCancel}
                        title="Anulowanie treningu"
                        message="Czy na pewno chcesz anulować ten trening?"
                    />
                )}
            </div>
        </OwnerLayout>
    );
};

export default OwnerCalendar;